// 📦 importy
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import Compressor from "compressorjs";
import "../styles/ViewerPage.css";
import PolaroidCamera from "../components/PolaroidCamera";

const ViewerPage = () => {
  const imageProxyUrl = process.env.REACT_APP_Get_Image_Proxy;
  const verifypasswordUrl = process.env.REACT_APP_Verify_Password;
  const updateimageUrl = process.env.REACT_APP_Update_Image;
  const setPasswordUrl = process.env.REACT_APP_Set_Password;
  const updateAppIdUrl = process.env.REACT_APP_Update_AppId;

  const { id } = useParams();

  const [imageSrc, setImageSrc] = useState("");
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [showCamera, setShowCamera] = useState(true);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [isPasswordCorrect, setIsPasswordCorrect] = useState(false);
  const [requireNewPassword, setRequireNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("");
  const [appChangeStatus, setAppChangeStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isChangingApp, setIsChangingApp] = useState(false);
  const [selectedApp, setSelectedApp] = useState("");
  const gifUrlRef = useRef(null);

  useEffect(() => {
    const loadImage = async () => {
      try {
        const response = await fetch(`${imageProxyUrl}/${id}`);
        const data = await response.json();

        if (!data.signedUrl) {
          setErrorMessage("Nie udało się pobrać obrazu.");
          return;
        }

        const img = new Image();
        img.src = data.signedUrl;
        img.onload = () => {
          setImageSrc(data.signedUrl);
          setIsImageLoaded(true);
          setTimeout(() => setShowCamera(false), 300);
        };
      } catch {
        setErrorMessage("Nie udało się pobrać obrazu.");
      }
    };

    loadImage();
  }, [id]);

  useEffect(() => {
    document.body.style.overflowY = "auto";
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  // 🧹 Czyść błąd przy zmianie aplikacji
  useEffect(() => {
    setErrorMessage("");
  }, [selectedApp]);

  const checkPassword = async () => {
    setStatus("🔍 Sprawdzanie hasła...");
    setErrorMessage("");
    setIsLoading(true);

    try {
      const response = await fetch(`${verifypasswordUrl}/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.success) {
        if (data.requireNewPassword) {
          setRequireNewPassword(true);
        } else {
          setIsPasswordCorrect(true);
        }
        setStatus("");
      } else {
        setErrorMessage("❌ Niepoprawne hasło!");
        setStatus("");
      }
    } catch {
      setErrorMessage("❌ Błąd podczas sprawdzania hasła.");
      setStatus("");
    }

    setIsLoading(false);
  };

  const submitNewPassword = async () => {
    if (newPassword.length < 4) {
      setErrorMessage("❌ Hasło musi mieć min. 4 znaki.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("❌ Hasła nie są takie same.");
      return;
    }

    setIsLoading(true);
    setStatus("🔐 Ustawianie nowego hasła...");

    try {
      const response = await fetch(`${setPasswordUrl}/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword }),
      });

      const data = await response.json();

      if (data.success) {
        setIsPasswordCorrect(true);
        setRequireNewPassword(false);
        setShowPasswordPrompt(true);
        setErrorMessage("");
        setStatus("✅ Hasło zmienione.");
        setTimeout(() => setStatus(""), 1000);
      } else {
        setErrorMessage("❌ Nie udało się zapisać hasła.");
      }
    } catch {
      setErrorMessage("❌ Błąd połączenia.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type === "image/gif") {
      setNewFile(file);
      return;
    }

    new Compressor(file, {
      quality: 0.4,
      maxWidth: 1024,
      success: setNewFile,
      error: () => setNewFile(file),
    });
  };

  const handleImageChange = async () => {
    if (!newFile) {
      setErrorMessage("❌ Wybierz nowe zdjęcie!");
      return;
    }

    setIsLoading(true);
    setStatus("📡 Wysyłanie nowego zdjęcia...");
    setErrorMessage("");

    const formData = new FormData();
    formData.append("password", password);
    formData.append("file", newFile);

    try {
      const response = await fetch(`${updateimageUrl}/${id}`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setStatus("✅ Zdjęcie zostało przesłane!");
        setIsSuccess(true);
        if (newFile.type === "image/gif") {
          const gifUrl = URL.createObjectURL(newFile);
          gifUrlRef.current = gifUrl;
          setImageSrc(gifUrl);
        } else {
          gifUrlRef.current = null;
          setTimeout(() => {
            setImageSrc(`${imageProxyUrl}/${id}?cache=${Date.now()}`);
          }, 500);
        }
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setErrorMessage("❌ Błąd podczas podmiany zdjęcia!");
      }
    } catch {
      setErrorMessage("❌ Błąd podczas przesyłania zdjęcia.");
    } finally {
      setIsLoading(false);
    }
  };

  const changeApp = async () => {
    if (!selectedApp) {
      setErrorMessage("❌ Wybierz aplikację.");
      return;
    }

    setIsLoading(true);
    setAppChangeStatus("🔄 Zmieniam aplikację...");

    try {
      const response = await fetch(`${updateAppIdUrl}/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ app_id: selectedApp }),
      });

      const data = await response.json();

      if (data.success) {
        setAppChangeStatus("✅ Zmieniono aplikację!");
        setTimeout(() => window.location.href = `/${id}`, 1500);
      } else {
        setErrorMessage("❌ Nie udało się zmienić aplikacji.");
      }
    } catch {
      setErrorMessage("❌ Błąd serwera.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {showCamera && <PolaroidCamera />}
      <div className="scroll-spacer"></div>
      {!showCamera && (
        <div className="viewer-container">
          <h2 className="viewer-title">
            <img src="/LogoV2.svg" alt="📷 PinkTouch" className="logo-img" />
          </h2>

          {!showPasswordPrompt && (
            <>
              <div className="viewer-content">
                <img src={imageSrc} alt="Przesłane zdjęcie" className="viewer-image fade-in" />
              </div>
              <button className="viewer-edit-button" onClick={() => {
                setShowPasswordPrompt(true);
                setErrorMessage("");
              }}>
                Edytuj
              </button>
            </>
          )}

          {showPasswordPrompt && !isPasswordCorrect && !requireNewPassword && (
            <div className="viewer-password-container">
              <h3 className="viewer-password-title">🔑 Podaj hasło, aby edytować</h3>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="viewer-password-input"
                placeholder="Wpisz hasło"
              />
              <button className="viewer-password-button" onClick={checkPassword} disabled={isLoading}>
                {isLoading ? "🔄 Sprawdzanie..." : "Sprawdź"}
              </button>
              <button className="viewer-cancel-button" onClick={() => {
                setShowPasswordPrompt(false);
                setIsPasswordCorrect(false);
                setPassword("");
                setErrorMessage("");
                setStatus("");
              }}>
                Powrót do zdjęcia
              </button>
              {errorMessage && <p className="viewer-error">{errorMessage}</p>}
            </div>
          )}

          {requireNewPassword && (
            <div className="viewer-password-container">
              <h3 className="viewer-password-title">🔐 Ustaw nowe hasło</h3>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Nowe hasło" className="viewer-password-input" />
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Powtórz hasło" className="viewer-password-input" />
              <button className="viewer-password-button" onClick={submitNewPassword} disabled={isLoading}>
                {isLoading ? "🔄 Zapisuję..." : "Zapisz nowe hasło"}
              </button>
              <button className="viewer-cancel-button" onClick={() => {
                setRequireNewPassword(false);
                setPassword("");
                setErrorMessage("");
                setStatus("");
              }}>
                Anuluj
              </button>
              {errorMessage && <p className="viewer-error">{errorMessage}</p>}
            </div>
          )}

          {isPasswordCorrect && showPasswordPrompt && !isChangingApp && (
            <div className="viewer-upload-container">
              <h3 className="viewer-edit-title">📤 Wgraj nowe zdjęcie</h3>
              <input type="file" accept="image/*" onChange={handleFileChange} className="viewer-file-input" />
              <button className="viewer-upload-button" onClick={handleImageChange} disabled={isLoading}>
                {isLoading ? "🔄 Wysyłanie..." : status || "Zmień obraz"}
              </button>
              <button className="viewer-changeapp-button" onClick={() => {
                setIsChangingApp(true);
                setErrorMessage("");
              }}>
                Zmień aplikację
              </button>
              <button className="viewer-cancel-button" onClick={() => {
                setShowPasswordPrompt(false);
                setIsPasswordCorrect(false);
                setPassword("");
                setErrorMessage("");
                setStatus("");
              }}>
                Powrót do podglądu
              </button>
              {errorMessage && <p className="viewer-error">{errorMessage}</p>}
            </div>
          )}

          {isPasswordCorrect && isChangingApp && (
            <div className="viewer-password-container">
              <h3 className="viewer-password-title">⚙️ Wybierz aplikację</h3>

              <div className="viewer-radio-wrapper">
  {["viewer", "geocar"].map((app) => (
    <label key={app} className={`viewer-radio-card ${selectedApp === app ? "selected" : ""}`}>
      <input
        type="radio"
        value={app}
        checked={selectedApp === app}
        onChange={() => setSelectedApp(app)}
      />
      <div className="checkmark" />
      <span className="app-label">{app === "viewer" ? "PinkFoto" : "PinkCar"}</span>
    </label>
  ))}
</div>


<button
  className={`viewer-password-button ${isLoading ? "loading" : ""} ${appChangeStatus.includes("✅") ? "success" : ""}`}
  onClick={changeApp}
  disabled={isLoading}
>
  {isLoading
    ? "🔄 Zmieniam aplikację..."
    : appChangeStatus.includes("✅")
    ? "✅ Zmieniono!"
    : "Zapisz i przejdź"}
</button>

<button
  className="viewer-cancel-button"
  onClick={() => {
    setIsChangingApp(false);
    setErrorMessage("");
    setAppChangeStatus("");
  }}
>
  Cofnij
</button>

{errorMessage && <p className="viewer-error">{errorMessage}</p>}

            </div>
          )}

          <div className="scroll-spacer"></div>
          <div className="viewer-footer">
            <a href="https://pinktouch.store/regulamin" target="_blank" rel="noopener noreferrer" className="viewer-link">
              Regulamin Aplikacji
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewerPage;
